import React, { useEffect, useId, useRef } from 'react';
import { cn } from '@/lib/utils';

export interface GlassTab {
    id: string;
    label: string;
    icon: React.ReactNode;
    badge?: React.ReactNode;
}

interface GlassTabbarProps {
    tabs: GlassTab[];
    activeId: string;
    onSelect: (id: string) => void;
    leftSlot?: React.ReactNode;
    rightSlot?: React.ReactNode;
    accent?: string;
    className?: string;
}

/**
 * Portals-Market style Liquid-Glass bottom nav.
 * Faithful port: SVG distortion filters + color-mix glass + sliding pill indicator
 * driven by [data-active]/[data-previous] with directional scale keyframes.
 */
export const GlassTabbar: React.FC<GlassTabbarProps> = ({
    tabs,
    activeId,
    onSelect,
    leftSlot,
    rightSlot,
    accent = '#3390EC',
    className,
}) => {
    const uid = useId().replace(/[:-]/g, '');
    const distortionId = `gt-dist-${uid}`;
    const glassDistortionId = `gt-glass-dist-${uid}`;
    const switcherRef = useRef<HTMLDivElement>(null);
    const prevRef = useRef(-1);

    const activeIndex = tabs.findIndex(t => t.id === activeId);

    // Portals-style: store previous index, debounce 100ms, then update attributes
    useEffect(() => {
        const idx = activeIndex;
        if (idx === -1) { prevRef.current = -1; return; }
        const t = setTimeout(() => {
            const el = switcherRef.current;
            if (!el) return;
            if (prevRef.current !== -1 && prevRef.current !== idx) {
                el.setAttribute('data-previous', String(prevRef.current));
            }
            el.setAttribute('data-active', String(idx));
            prevRef.current = idx;
        }, 100);
        return () => clearTimeout(t);
    }, [activeIndex]);

    const glassShadow =
        'inset 0 0 0 1px rgba(255,255,255,.03), inset 1.8px 3px 0px -2px rgba(255,255,255,.27), inset -2px -2px 0px -2px rgba(255,255,255,.24), inset -3px -8px 1px -6px rgba(255,255,255,.18), inset -.3px -1px 4px 0px rgba(0,0,0,.24), inset -1.5px 2.5px 0px -2px rgba(0,0,0,.4), inset 0px 3px 4px -2px rgba(0,0,0,.4), inset 2px -6.5px 1px -4px rgba(0,0,0,.2), 0px 1px 5px 0px rgba(0,0,0,.2), 0px 6px 16px 0px rgba(0,0,0,.16)';

    const css = `
        .gt-root-${uid} {
            --gt-height: 62px;
            --gt-accent: ${accent};
            --gt-bg-glass: #000;
            --gt-c-light: #fff;
            --gt-c-dark: #000;
            --gt-c-content: #ffffff;
            --gt-saturation: 140%;
            --gt-switcher-transition: .25s cubic-bezier(1, 0, .4, 1);
            --gt-glass-shadow: ${glassShadow};
        }
        .gt-root-${uid} .gt-row {
            display: flex;
            align-items: stretch;
            gap: 12px;
            height: var(--gt-height);
        }
        /* --- Glass circles (left/right slots) --- */
        .gt-root-${uid} .gt-circle {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            flex-shrink: 0;
            width: 58px;
            height: 100%;
            border-radius: 99em;
            background-color: color-mix(in srgb, var(--gt-bg-glass) 12%, transparent);
            -webkit-backdrop-filter: blur(8px) saturate(var(--gt-saturation));
            backdrop-filter: blur(8px) saturate(var(--gt-saturation));
            box-shadow: var(--gt-glass-shadow);
            border: none;
            cursor: pointer;
            padding: 4px;
            box-sizing: border-box;
        }
        .gt-root-${uid} .gt-circle-solid {
            background-color: #282727f2;
            -webkit-backdrop-filter: blur(8px);
            backdrop-filter: blur(8px);
        }
        .gt-root-${uid} .gt-circle-title {
            font-weight: 700;
            font-size: 10px;
            text-align: center;
            line-height: 1.2;
            color: #ffffff66;
            letter-spacing: -.4px;
            transition: color .16s;
        }
        .gt-root-${uid} .gt-circle.active .gt-circle-title { color: #fff; }
        .gt-root-${uid} .gt-profile-avatar {
            border: 2px solid #007aff;
            border-radius: 99em;
            transition: border-color .2s;
        }
        .gt-root-${uid} .gt-circle.active .gt-profile-avatar { border-color: var(--gt-accent); }
        /* --- Hidden SVG filters --- */
        .gt-root-${uid} .gt-filter {
            position: absolute;
            width: 0;
            height: 0;
            z-index: -1;
            pointer-events: none;
        }
        /* --- Sliding glass capsule --- */
        .gt-root-${uid} .gt-switcher {
            --tab-accent: var(--gt-accent);
            --tab-count: ${tabs.length};
            position: relative;
            display: grid;
            grid-template-columns: repeat(var(--tab-count), 1fr);
            align-items: center;
            gap: 0px;
            width: auto;
            flex: 1 1 auto;
            min-width: 0;
            height: 100%;
            padding: 4px;
            box-sizing: border-box;
            border: none;
            border-radius: 99em;
            background-color: color-mix(in srgb, var(--gt-bg-glass) 12%, transparent);
            -webkit-backdrop-filter: blur(8px) url(#${distortionId}) saturate(var(--gt-saturation));
            backdrop-filter: blur(8px) url(#${distortionId}) saturate(var(--gt-saturation));
            isolation: isolate;
            box-shadow: var(--gt-glass-shadow);
            transition: background-color var(--gt-switcher-transition), box-shadow var(--gt-switcher-transition);
        }
        .gt-root-${uid} .gt-switcher:after {
            content: "";
            position: absolute;
            left: 4px;
            top: 4px;
            display: block;
            width: calc((100% - 8px) / var(--tab-count));
            height: 54px;
            border-radius: 99em;
            background-color: #0000004d;
            z-index: -1;
            box-shadow: inset 0 0 0 1px #ffffff08, inset 2px 1px 0 -1px #ffffff45, inset -1.5px -1px 0 -1px #ffffff3d, inset -2px -6px 1px -5px #ffffff2e, inset -1px 2px 3px -1px #0006, inset 0 -4px 1px -2px #0003, 0 3px 6px #00000029;
            transition: background-color var(--gt-switcher-transition), box-shadow var(--gt-switcher-transition), translate var(--gt-switcher-transition);
        }
        ${tabs.map((_, i) => `
        .gt-root-${uid} .gt-switcher[data-active="${i}"]:after {
            translate: ${i * 100}% 0;
            transform-origin: ${i === 0 ? 'right' : i === (tabs.length - 1) ? 'left' : 'center'};
            animation: gt-toggle-${uid}-${i} .44s ease;
        }`).join('')}
        ${tabs.flatMap((_, i) => tabs.map((_, j) => i !== j ? `
        .gt-root-${uid} .gt-switcher[data-previous="${i}"][data-active="${j}"]:after { transform-origin: ${j > i ? 'left' : 'right'}; }` : '')).join('')}
        .gt-root-${uid} .gt-switcher[data-active="-1"]:after { display: none; }
        ${tabs.map((_, i) => `
        @keyframes gt-toggle-${uid}-${i} { 0% { scale: 1 1; } 50% { scale: ${i % 2 === 0 ? 1.05 : 1.08} 1.1; } 100% { scale: 1 1; } }`).join('')}
        /* --- Options --- */
        .gt-root-${uid} .gt-optionWrap { position: relative; width: 100%; height: 100%; display: flex; }
        .gt-root-${uid} .gt-option {
            --c: var(--gt-c-content);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 4px;
            padding: 0 12px;
            width: 100%;
            height: 100%;
            box-sizing: border-box;
            border-radius: 99em;
            background: transparent;
            border: none;
            cursor: pointer;
            opacity: .4;
            color: var(--c);
            transition: all .16s;
            position: relative;
            z-index: 1;
            flex-shrink: 0;
        }
        .gt-root-${uid} .gt-option:hover { opacity: .7; cursor: pointer; }
        .gt-root-${uid} .gt-option:hover .gt-icon { scale: 1.1; }
        .gt-root-${uid} .gt-optionActive {
            --c: var(--tab-accent, var(--gt-accent));
            opacity: 1;
            cursor: auto;
            color: var(--c);
        }
        .gt-root-${uid} .gt-optionActive:hover { opacity: 1; cursor: pointer; }
        .gt-root-${uid} .gt-optionActive:hover .gt-icon { scale: 1; }
        .gt-root-${uid} .gt-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            transition: scale .2s cubic-bezier(.5, 0, 0, 1);
            color: currentColor;
            position: relative;
        }
        .gt-root-${uid} .gt-icon svg { width: 24px; height: 24px; }
        .gt-root-${uid} .gt-title {
            font-weight: 500;
            font-size: 10px;
            text-align: center;
            line-height: 1.2;
            color: currentColor;
        }
    `;

    return (
        <div className={cn(`gt-root-${uid}`, className)}>
            <style>{css}</style>
            <svg className={`gt-filter`} aria-hidden="true">
                <filter id={distortionId} x="0%" y="0%" width="100%" height="100%">
                    <feTurbulence type="fractalNoise" baseFrequency="0.008 0.008" numOctaves="2" seed="92" result="noise" />
                    <feGaussianBlur in="noise" stdDeviation="2" result="blurred" />
                    <feDisplacementMap in="SourceGraphic" in2="blurred" scale="70" xChannelSelector="R" yChannelSelector="G" />
                </filter>
                <filter id={glassDistortionId} x="0%" y="0%" width="100%" height="100%" filterUnits="objectBoundingBox">
                    <feTurbulence type="fractalNoise" baseFrequency="0.001 0.005" numOctaves="1" seed="17" result="turbulence" />
                    <feComponentTransfer in="turbulence" result="mapped">
                        <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
                        <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
                        <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
                    </feComponentTransfer>
                    <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
                    <feSpecularLighting in="softMap" surfaceScale="5" specularConstant="1" specularExponent="100" lightingColor="white" result="specLight">
                        <fePointLight x="-200" y="-200" z="300" />
                    </feSpecularLighting>
                    <feComposite in="specLight" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="litImage" />
                    <feDisplacementMap in="SourceGraphic" in2="softMap" scale="200" xChannelSelector="R" yChannelSelector="G" />
                </filter>
            </svg>

            <div className="gt-row">
                {leftSlot}

                <div
                    ref={switcherRef}
                    className="gt-switcher"
                    data-active={activeIndex > -1 ? activeIndex : -1}
                    data-tabs={tabs.length}
                >
                    {tabs.map((tab) => (
                        <div key={tab.id} className="gt-optionWrap">
                            <button
                                type="button"
                                onClick={() => onSelect(tab.id)}
                                className={cn('gt-option', tab.id === activeId && 'gt-optionActive')}
                            >
                                <span className="gt-icon">
                                    {tab.icon}
                                    {tab.badge}
                                </span>
                                <span className="gt-title">{tab.label}</span>
                            </button>
                        </div>
                    ))}
                </div>

                {rightSlot}
            </div>
        </div>
    );
};

export default GlassTabbar;